// React access to settings. Changes are saved and applied to <html> at once.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  applySettings,
  browserStorage,
  loadSettings,
  mergeSettings,
  saveSettings,
  type Settings,
  type SettingsPatch,
} from "./settings";

interface SettingsValue {
  settings: Settings;
  update: (patch: SettingsPatch) => void;
}

const SettingsCtx = createContext<SettingsValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => loadSettings(browserStorage()));

  useEffect(() => {
    applySettings(settings, document.documentElement);
    saveSettings(browserStorage(), settings);
  }, [settings]);

  const update = useCallback((patch: SettingsPatch) => setSettings((s) => mergeSettings(s, patch)), []);
  const value = useMemo(() => ({ settings, update }), [settings, update]);
  return <SettingsCtx.Provider value={value}>{children}</SettingsCtx.Provider>;
}

export function useSettings(): SettingsValue {
  const v = useContext(SettingsCtx);
  if (!v) throw new Error("useSettings outside SettingsProvider");
  return v;
}
